"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Search,
  Calendar,
  Clock,
  ArrowRight,
  GraduationCap,
  Sparkles,
  MapPin,
  ClipboardList,
  Compass
} from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { GUIDES, Guide } from "@/lib/guides-data";

export function GuidesContent() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<"all" | "counseling" | "colleges" | "strategy" | "eligibility">("all");

  const filteredGuides = GUIDES.filter((guide) => {
    const matchesSearch =
      guide.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guide.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guide.keywords.some((keyword) => keyword.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === "all" || guide.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const categories = [
    { id: "all", name: "All Resources", icon: BookOpen },
    { id: "counseling", name: "Counseling Process", icon: Compass },
    { id: "colleges", name: "College Comparisons", icon: GraduationCap },
    { id: "strategy", name: "Choice Strategy", icon: Sparkles },
    { id: "eligibility", name: "Eligibility Rules", icon: ClipboardList }
  ] as const;

  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case "counseling":
        return "bg-sky-500/10 text-sky-600 border-sky-500/20 dark:bg-sky-500/20 dark:text-sky-300";
      case "colleges":
        return "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:bg-emerald-500/20 dark:text-emerald-300";
      case "strategy":
        return "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:bg-amber-500/20 dark:text-amber-300";
      case "eligibility":
        return "bg-rose-500/10 text-rose-600 border-rose-500/20 dark:bg-rose-500/20 dark:text-rose-300";
      default:
        return "bg-primary/10 text-primary border-primary/20";
    }
  };

  const getCategoryLabel = (cat: string) => {
    switch (cat) {
      case "counseling":
        return "Counseling Process";
      case "colleges":
        return "Colleges & Placements";
      case "strategy":
        return "Choice Strategy";
      case "eligibility":
        return "Eligibility & Verification";
      default:
        return "General";
    }
  };

  return (
    <div className="min-h-screen bg-background selection:bg-primary/30 pt-10 pb-20 overflow-hidden">
      {/* Decorative Background Gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full -z-10 pointer-events-none">
        <div className="absolute top-[-5%] left-[-15%] w-[60%] h-[40%] bg-primary/10 blur-[130px] rounded-full opacity-60" />
        <div className="absolute bottom-[20%] right-[-15%] w-[60%] h-[40%] bg-secondary/10 blur-[130px] rounded-full opacity-60" />
      </div>

      <div className="container mx-auto px-6 max-w-6xl">
        {/* Hero Section */}
        <section className="text-center py-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-container/50 border border-outline-variant/10 text-primary text-sm font-bold mb-6 shadow-sm backdrop-blur-md"
          >
            <BookOpen className="size-4" />
            <span>Admission Resources</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-3xl md:text-5xl font-display font-black tracking-tight text-foreground mb-6 leading-tight"
          >
            CCMT M.Tech Counseling Guides
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-base md:text-lg text-on-surface-variant max-w-3xl mx-auto leading-relaxed"
          >
            Master your post-GATE admission journey. Read through our expert-authored, unique, and comprehensive guides designed to demystify choice-filling, cutoff fluctuations, seat allotments, and verification processes.
          </motion.p>
        </section>

        {/* Filter and Search Bar */}
        <section className="mb-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Search Input */}
            <div className="relative w-full md:w-80 lg:w-96 shrink-0">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant/70 size-5" />
              <input
                type="text"
                placeholder="Search guides by title, keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-outline-variant/20 bg-surface-container/60 px-4 pl-12 text-sm text-foreground placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/50 backdrop-blur-md transition-all shadow-sm"
              />
            </div>

            {/* Category Filter Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1 md:pb-0 -mx-6 px-6 md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] md:overflow-x-visible md:flex-wrap md:justify-end w-full">
              {categories.map((cat) => {
                const Icon = cat.icon;
                const isActive = selectedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center gap-2 px-4 h-11 rounded-xl border text-xs font-bold transition-all shrink-0 snap-start ${
                      isActive
                        ? "bg-primary text-primary-foreground border-primary shadow-md hover:opacity-95"
                        : "bg-surface-container/40 border-outline-variant/10 text-on-surface-variant hover:bg-surface-container hover:text-foreground"
                    }`}
                  >
                    <Icon className="size-3.5" />
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Guides List Grid */}
        <section className="mb-20">
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <AnimatePresence mode="popLayout">
              {filteredGuides.length > 0 ? (
                filteredGuides.map((guide, idx) => (
                  <motion.div
                    key={guide.slug}
                    layout
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -20 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    whileHover={{ y: -6 }}
                    className="flex flex-col justify-between p-6 rounded-2xl bg-surface-container border border-outline-variant/15 hover:border-primary/25 transition-all duration-300 shadow-sm"
                  >
                    <div>
                      {/* Meta Tags */}
                      <div className="flex items-center justify-between mb-4">
                        <span className={`px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider rounded-md border ${getCategoryColor(guide.category)}`}>
                          {getCategoryLabel(guide.category)}
                        </span>
                        <div className="flex items-center gap-3 text-on-surface-variant/60 text-xs">
                          <span className="flex items-center gap-1">
                            <Clock className="size-3" />
                            {guide.readTime}
                          </span>
                        </div>
                      </div>

                      {/* Title & Desc */}
                      <h2 className="text-lg md:text-xl font-bold text-foreground mb-3 line-clamp-2 hover:text-primary transition-colors">
                        <Link href={`/counseling-guides/${guide.slug}`}>{guide.title}</Link>
                      </h2>
                      <p className="text-on-surface-variant text-sm leading-relaxed mb-6 line-clamp-3">
                        {guide.description}
                      </p>
                    </div>

                    {/* Bottom CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-outline-variant/10 mt-auto">
                      <span className="flex items-center gap-1 text-[11px] font-bold text-on-surface-variant/60">
                        <Calendar className="size-3" />
                        {guide.publishedDate}
                      </span>
                      <Link href={`/counseling-guides/${guide.slug}`}>
                        <Button variant="ghost" size="sm" className="font-bold text-xs gap-1.5 hover:text-primary hover:bg-primary/5 px-3 rounded-lg">
                          Read Guide
                          <ArrowRight className="size-3.5" />
                        </Button>
                      </Link>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="col-span-full py-20 text-center rounded-3xl bg-surface-container/40 border border-outline-variant/10 text-on-surface-variant">
                  <BookOpen className="size-16 text-primary/20 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-foreground mb-2">No guides found</h3>
                  <p className="text-sm max-w-md mx-auto">
                    We couldn't find any counseling articles matching "{searchTerm}". Try refining your keywords or clearing the category filters.
                  </p>
                </div>
              )}
            </AnimatePresence>
          </motion.div>
        </section>

        {/* Interactive Strategy CTA */}
        <section className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 md:p-10 rounded-3xl bg-primary/5 border border-primary/20 text-center relative overflow-hidden shadow-sm"
          >
            <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none" />
            <div className="relative z-10 space-y-6">
              <h3 className="text-xl md:text-2xl font-display font-black text-foreground">
                Ready to estimate your M.Tech admission chances?
              </h3>
              <p className="text-on-surface-variant max-w-xl mx-auto text-sm md:text-base">
                Combine your guide readings with our live calculation tools. Use the Predictor to filter eligible NITs and IIITs based on your exact GATE scorecard.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link href="/predictor">
                  <Button className="h-12 px-6 rounded-xl font-bold gap-2">
                    Try Predictor
                  </Button>
                </Link>
                <Link href="/explorer">
                  <Button variant="outline" className="h-12 px-6 rounded-xl font-bold bg-background/50">
                    Explore Cutoffs
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
}
